import { InfoCircleOutlined } from '@ant-design/icons';
import { Collapse, Table, Tooltip } from 'antd';
import classNames from 'classnames';
import React, { Fragment, useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { HowDoesItWork } from 'app/components/commons/HowDoesItWork/HowDoesItWork';
import type { RoomsWithInventory } from 'app/typings/autoInventory';
import { AutoInventorySteps } from 'app/typings/autoInventory';
import type { ExtranetPackage } from 'app/typings/packages';
import { format } from 'app/utils/dayjs';

import type { FormValues, PackagePricesPreview } from '../../../Utils';
import {
  associateRooms,
  getHDIWDetails,
  organizePreviewDataByDate,
} from '../../../Utils';

import './Preview.scss';

const { Panel } = Collapse;

type PreviewProps = {
  packages: ExtranetPackage[];
  scrappingInventory: RoomsWithInventory[];
  formData: FormValues;
};

export const Preview = ({
  packages,
  scrappingInventory,
  formData,
}: PreviewProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);

  const filteredRooms = associateRooms(formData, scrappingInventory);

  const formatDataSource = (organizedData: {
    dates: string[];
    stocks: number[];
    bars: number[];
    packagePrices: PackagePricesPreview[];
  }) => {
    return [
      {
        key: 'Stock',
        timePeriod: <div>Remaining Stock</div>,
        ...organizedData.dates.reduce((acc, _, index) => {
          acc[`date${index}`] = organizedData.stocks[index];

          return acc;
        }, {} as Record<string, string | number | React.ReactNode>),
      },
      {
        key: 'Bar',
        timePeriod: (
          <div>
            BAR RO (NA/NR)
            <Tooltip
              overlayClassName="auto-inventory-tooltip-stock"
              title="BAR is the Best Available Rate (Non Amendable/Non Refundable) on your website on each day, for each room category"
            >
              <span className="grey-icon margin-left">
                <InfoCircleOutlined />
              </span>
            </Tooltip>
          </div>
        ),
        ...organizedData.dates.reduce((acc, _, index) => {
          acc[`date${index}`] = (
            <div className="bar">
              {currencyFormatter(organizedData.bars[index] || 0)}
            </div>
          );

          return acc;
        }, {} as Record<string, string | number | React.ReactNode>),
      },
    ];
  };

  const formatPackageDataSource = (
    organizedData: {
      dates: string[];
      bars: number[];
      packagePrices: PackagePricesPreview[];
    },
    pkg: ExtranetPackage
  ) => {
    const pkgIndex = organizedData.packagePrices.findIndex(
      (pp) => pp.packageId === pkg.id
    );

    const experienceValue =
      organizedData.packagePrices[pkgIndex].packageValues[0] -
      organizedData.bars[0];

    const renderDiscountPercent = (value: number) => {
      if (value === 0) {
        return '0%';
      }

      return `-${value}%`;
    };

    return [
      {
        key: 'Discount Price',
        timePeriod: (
          <div>
            Staycation rate
            <Tooltip
              overlayClassName="auto-inventory-tooltip-stock"
              title="BAR is the Best Available Rate (Non Amendable/Non Refundable) on your website on each day, for each room category"
            >
              <span className="grey-icon margin-left">
                <InfoCircleOutlined />
              </span>
            </Tooltip>
          </div>
        ),
        ...organizedData.dates.reduce((acc, _, index) => {
          const color = organizedData.packagePrices[pkgIndex].colors[index];

          acc[`date${index}`] = (
            <div className={classNames(`${color}-font`)}>
              <div
                className={classNames(
                  'inventory-cell',
                  'readonly',
                  'ai-preview'
                )}
              >
                {currencyFormatter(
                  organizedData.packagePrices[pkgIndex].staycationRates[
                    index
                  ] || 0
                )}
              </div>
              <span className="description">
                <span className="crossed">
                  {currencyFormatter(
                    organizedData.packagePrices[pkgIndex].packageValues[
                      index
                    ] || 0
                  )}
                </span>
              </span>
            </div>
          );

          return acc;
        }, {} as Record<string, string | number | React.ReactNode>),
      },
      {
        key: 'Discount',
        timePeriod: (
          <div>
            Discount
            <Tooltip
              overlayClassName="auto-inventory-tooltip-discount"
              title={`The minimum discount rate is 18%, on the total value of the package
              Staycation rate = BAR + Package (${currencyFormatter(
                experienceValue || 0
              )}) - Staycation discount`}
            >
              <span className="grey-icon margin-left">
                <InfoCircleOutlined />
              </span>
            </Tooltip>
          </div>
        ),
        ...organizedData.dates.reduce((acc, _, index) => {
          acc[`date${index}`] = (
            <div>
              {renderDiscountPercent(
                organizedData.packagePrices[pkgIndex].discounts[index]
              )}
            </div>
          );

          return acc;
        }, {} as Record<string, string | number | React.ReactNode>),
      },
    ];
  };

  return (
    <div className="preview-step">
      <div className="explanation">
        Based on your configuration this is what we have identified:
      </div>
      <div className="collapses">
        <Collapse className="collapse-container">
          {filteredRooms &&
            filteredRooms.map((room, idx) => {
              const organizedData = organizePreviewDataByDate(
                room.inventory,
                packages,
                formData
              );

              const dataSource = formatDataSource(organizedData);

              const packageDataSource = (pkg: ExtranetPackage) =>
                formatPackageDataSource(organizedData, pkg);

              const columns = [
                {
                  title: (
                    <div className="header-table">
                      <span>Time period</span>
                    </div>
                  ),
                  dataIndex: 'timePeriod',
                  key: 'timePeriod',
                },
                ...organizedData.dates.map((date, index) => ({
                  title: (
                    <div className="header-table date">
                      <span>{format(new Date(date), 'ddd')}</span>
                      {format(new Date(date), 'DD/MM')}
                    </div>
                  ),
                  dataIndex: `date${index}`,
                  key: `date${index}`,
                })),
              ];

              return (
                <Panel header={room.name} key={room.id}>
                  <Table
                    className="preview-table"
                    dataSource={dataSource}
                    columns={columns}
                    pagination={false}
                    rowClassName={(record) =>
                      `${record.key.replace(/\s/g, '').toLowerCase() + '-row'}`
                    }
                    bordered
                  />
                  {packages.map((pkg) => (
                    <Fragment key={pkg.id}>
                      <div
                        className="package-header"
                        key={`${room.id}-${room.name}-${pkg.id}-header`}
                      >
                        {pkg.aliasForHotel}
                      </div>
                      <Table
                        key={`${room.id}-${room.name}-${pkg.id}-table`}
                        className="preview-table"
                        dataSource={packageDataSource(pkg)}
                        columns={columns}
                        showHeader={false}
                        pagination={false}
                        rowClassName={(record) =>
                          `${
                            record.key.replace(/\s/g, '').toLowerCase() + '-row'
                          }`
                        }
                      />
                    </Fragment>
                  ))}
                </Panel>
              );
            })}
        </Collapse>
      </div>
      <HowDoesItWork
        items={getHDIWDetails(AutoInventorySteps.preview)}
        trackFrom="auto-inventory"
        stepFrom={AutoInventorySteps.preview}
      />
    </div>
  );
};
